import { createAsyncThunk, createEntityAdapter, createSlice } from '@reduxjs/toolkit';
import { makeRequest } from 'Douleutaras/services/apiClient';
import { crmStatusesSlice } from 'Douleutaras/redux/data/v2/crm/crmStatuses.Slice';
import { BookNowStatuses, ReduxEntity } from 'Douleutaras/types';
import { RootState } from 'Douleutaras/redux/store';
import { BookNowPatchInfo } from 'Douleutaras/redux/data/v2/crm/types';
import { bookNowEntriesSlice } from 'Douleutaras/redux/data/v2/crm/bookNowEntriesSlice';
import { AswParamsEnum, sendAswMessage } from 'Douleutaras/redux/data/v2/asw/aswSlice';
import { parseV3ErrorsInline } from 'Douleutaras/utilities/normalizers/api-errror-normalizer';

const defaultParams = {
  version: 'v2'
};

const crmAdapter = createEntityAdapter<CrmEntry>({
  selectId: item => item.job_id
});

interface CrmStatusesArgs {
  bid_ids: string;
}

export interface CrmStatus {
  level: number;
  message: {
    future: string;
    past: string;
  };
  positive_status: string;
}

export interface CrmNextStatusEntry {
  enabled: boolean;
  message: {
    past: string;
    future: string;
  };
  position: string;
  slug: BookNowStatuses;
}

export interface CrmEntry {
  job_id: number;
  level: number;
  next_statuses: Array<CrmNextStatusEntry>;
  skippable_levels: Array<number>;
  status_message: string;
}

export interface CrmStatusesOptions {
  all_crm_statuses: Array<CrmStatus>;
  crm_entries: Array<CrmEntry>;
}

const BOOK_NOW_ENTRY_ENDPOINT = 'api/public/book_now_entry/';

export const getCrmOptions = createAsyncThunk(
  'crm/options',
  async ({ bid_ids }: CrmStatusesArgs, api) => {
    const response = await makeRequest(BOOK_NOW_ENTRY_ENDPOINT, {
      method: 'OPTIONS',
      additionalGetParams: {
        bid_ids
      }
    })(api);

    const { crmStatusesAdded } = crmStatusesSlice.actions;

    if (response.data.all_crm_statuses) {
      api.dispatch(
        crmStatusesAdded(response.data.all_crm_statuses)
      );
    }

    return response?.data.crm_entries;
  }
);

interface PatchCrmEntryProps {
  bookNowId: string;
  data: BookNowPatchInfo & { list_page: boolean };
  sendCustomerMessage?: boolean;
  sendCustomerMessageParam?: AswParamsEnum;
  jobId?: string;
}

export const patchCrmEntry = createAsyncThunk<undefined, PatchCrmEntryProps>(
  'crm/entry/patch',
  async ({
           bookNowId,
           data,
           sendCustomerMessage,
           sendCustomerMessageParam,
           jobId
         },
         api
  ) => {
    const response = await makeRequest(`${BOOK_NOW_ENTRY_ENDPOINT}${bookNowId}`, {
      method: 'PATCH',
      body: data,
      additionalGetParams: defaultParams
    })(api);

    const { bookNowEntryAdd } = bookNowEntriesSlice.actions;

    if (response.status === 200 || response.status === 201) {
      if (sendCustomerMessage === true) {
        api.dispatch(sendAswMessage({ job: jobId, params: sendCustomerMessageParam }));
      }
      api.dispatch(bookNowEntryAdd(response.data));

      return response?.data;
    }

    throw new Error(parseV3ErrorsInline(response.payload.response.data));
  }
);

export const crmSlice = createSlice({
  name: 'crm',
  initialState: crmAdapter.getInitialState({
    entities: [],
    meta: {}
  } as ReduxEntity<CrmEntry>),
  reducers: {},
  extraReducers: builder => {
    builder.addCase(getCrmOptions.fulfilled, (state, action) => {
      crmAdapter.upsertMany(state, action.payload);
    });
  }
});

export const crmSelectors = crmAdapter.getSelectors((state: RootState) => state.crm);
